/** Provider globali e definizione delle rotte. */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { AdminLayout } from '@/features/admin/AdminLayout';
import { AdminLlmModelsPage } from '@/features/admin/AdminLlmModelsPage';
import { AdminLlmIncidentsPage } from '@/features/admin/AdminLlmIncidentsPage';
import { AdminLlmUsagePage } from '@/features/admin/AdminLlmUsagePage';
import { AdminUsersPage } from '@/features/admin/AdminUsersPage';
import { AutoTradingPage } from '@/features/auto-trading/AutoTradingPage';
import { ProposalTracePage } from '@/features/auto-trading/ProposalTracePage';
import { BriefingDebugPage } from '@/features/briefing/BriefingDebugPage';
import { NewsPage } from '@/features/news/NewsPage';
import { AutomationsPage } from '@/features/automations/AutomationsPage';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { LoginPage } from '@/features/auth/LoginPage';
import { BriefingPage } from '@/features/briefing/BriefingPage';
import { OutcomesPage } from '@/features/outcomes/OutcomesPage';
import { InstrumentBriefingHistoryPage } from '@/features/briefing/InstrumentBriefingHistoryPage';
import { SentimentHistoryPage } from '@/features/briefing/SentimentHistoryPage';
import { ConnectorsPage } from '@/features/connectors/ConnectorsPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ImportPage } from '@/features/imports/ImportPage';
import { DailyPickPage } from '@/features/daily-pick/DailyPickPage';
import { DiscoveryPage } from '@/features/discovery/DiscoveryPage';
import { DiscoveryRotationPage } from '@/features/discovery/DiscoveryRotationPage';
import { DiscoveryRunPage } from '@/features/discovery/DiscoveryRunPage';
import { InstrumentDetailPage } from '@/features/instruments/InstrumentDetailPage';
import { InstrumentSearchPage } from '@/features/instruments/InstrumentSearchPage';
import { BriefingDetailPage } from '@/features/markets/BriefingDetailPage';
import { MarketDetailPage } from '@/features/markets/MarketDetailPage';
import { MarketsPage } from '@/features/markets/MarketsPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { PortfolioDetailPage } from '@/features/portfolios/PortfolioDetailPage';
import { PortfolioInstrumentPage } from '@/features/portfolios/PortfolioInstrumentPage';
import { PortfolioPerformancePage } from '@/features/portfolios/PortfolioPerformancePage';
import { PortfolioPage } from '@/features/portfolios/PortfolioPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ReportTracePage } from '@/features/auto-trading/ReportTracePage';
import { AgentRunPage } from '@/features/agent-runs/AgentRunPage';
import { ReportsPage } from '@/features/reports/ReportsPage';
import { TradingLimitsPage } from '@/features/trading-limits/TradingLimitsPage';
import { AnalysisDetailPage } from '@/features/watchlist/AnalysisDetailPage';
import { WatchlistPage } from '@/features/watchlist/WatchlistPage';
import { TagPage } from '@/features/tags/TagPage';
import { WishlistPage } from '@/features/wishlist/WishlistPage';
import { AdminRoute } from './AdminRoute';
import { AppLayout } from './AppLayout';
import { ProtectedRoute } from './ProtectedRoute';
import { ThemeProvider } from './ThemeProvider';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // I dati arrivano da import manuali: non cambiano da soli mentre l'utente
      // guarda la pagina. Un refetch a ogni focus della finestra sarebbe rumore.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: (failureCount, error) => {
        // Un 4xx non migliora ritentando: solo gli errori di rete/5xx.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <ProtectedRoute>
                    <AppLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="portfolios" element={<PortfolioPage />} />
                <Route path="portfolios/:id" element={<PortfolioDetailPage />} />
                <Route path="portfolios/:id/performance" element={<PortfolioPerformancePage />} />
                <Route
                  path="portfolios/:id/instruments/:instrumentId"
                  element={<PortfolioInstrumentPage />}
                />
                <Route path="imports" element={<ImportPage />} />
                {/* F-085: la ricerca manuale multi-fonte, che era il segnaposto
                    «Strumenti» in sidebar. Rotta statica, quindi vince su
                    `:instrumentId` a prescindere dall'ordine. */}
                <Route path="instruments" element={<InstrumentSearchPage />} />
                {/* Dettaglio del titolo (SPEC-016): il posto dove uno strumento
                    esiste di per sé, fuori da watchlist/portafoglio/wishlist. */}
                <Route path="instruments/:instrumentId" element={<InstrumentDetailPage />} />
                <Route path="connectors" element={<ConnectorsPage />} />
                <Route path="watchlists" element={<WatchlistPage />} />
                <Route path="watchlists/analyses/:instrumentId/:analysisId" element={<AnalysisDetailPage />} />
                <Route path="wishlist" element={<WishlistPage />} />
                {/* F-080: ci si arriva cliccando un tag, da qualunque schermata. */}
                <Route path="tags/:tag" element={<TagPage />} />
                {/* Scoperta tematica (SPEC-015): l'imbuto verso watchlist e
                    dettaglio titolo. */}
                <Route path="discovery" element={<DiscoveryPage />} />
                {/* Sottomenu di Scopri: rotta sorella, non annidata — la pagina
                    ha viste proprie e annidarla avrebbe imposto un layout che
                    la scoperta tematica non ha. */}
                <Route path="discovery/daily-pick" element={<DailyPickPage />} />
                <Route path="discovery/rotations/:rotationId" element={<DiscoveryRotationPage />} />
              <Route path="discovery/:runId" element={<DiscoveryRunPage />} />
                <Route path="briefing" element={<BriefingPage />} />
                {/* Storico dei giudizi su un titolo (F-135): si apre dall'icona
                    nella riga di «Titoli valutati». */}
                <Route path="briefing/instrument/:instrumentId" element={<InstrumentBriefingHistoryPage />} />
                {/* Storico del sentiment (F-177): dal pulsante «Storico» della card. */}
                <Route path="briefing/sentiment" element={<SentimentHistoryPage />} />
                {/* Vista tecnica del digest (F-108): input e output grezzi. */}
                <Route path="briefing/:date/debug" element={<BriefingDebugPage />} />
                <Route path="markets" element={<MarketsPage />} />
                <Route path="news" element={<NewsPage />} />
                <Route path="markets/:mic" element={<MarketDetailPage />} />
                <Route path="markets/:mic/briefings/:briefingId" element={<BriefingDetailPage />} />
                <Route path="reports" element={<ReportsPage />} />
                {/* Tabellone degli esiti (F-126): il sistema aveva ragione? */}
                <Route path="outcomes" element={<OutcomesPage />} />
                {/* F-090: si apre dal proprio nome in sidebar. Raccoglie cio'
                    che riguarda l'account e non i dati di investimento. */}
                <Route path="profile" element={<ProfilePage />} />
                <Route path="settings/notifications" element={<NotificationsPage />} />
                <Route path="settings/trading-limits" element={<TradingLimitsPage />} />
                <Route path="auto-trading" element={<AutoTradingPage />} />
                {/* Vista tecnica di riconciliazione (F-096): raggiungibile dal registro
                    delle esecuzioni e dai movimenti di portafoglio. */}
                <Route path="auto-trading/proposals/:id" element={<ProposalTracePage />} />
                {/* Stessa riconciliazione, altro capo (F-105): si apre da un
                    resoconto invece che da un'operazione. Sotto `/reports`
                    perche' e' li' che si sta guardando quando ci si chiede
                    «e questo dove ha portato». */}
                <Route path="reports/:kind/:id/trace" element={<ReportTracePage />} />
                {/* F-144: il run agentico passo per passo. Vi si arriva dalla
                    riconciliazione di un'operazione e dal briefing che l'ha
                    prodotto: e' l'ultimo anello, quello che prima mancava. */}
                <Route path="agent-runs/:runId" element={<AgentRunPage />} />
                <Route path="automations" element={<AutomationsPage />} />
                {/*
                  Annidato dentro ProtectedRoute: `AdminRoute` legge `user.isAdmin`,
                  che esiste solo a sessione ripristinata. `/admin` reindirizza alla
                  prima sezione invece di essere una pagina vuota.
                */}
                <Route
                  path="admin"
                  element={
                    <AdminRoute>
                      <AdminLayout />
                    </AdminRoute>
                  }
                >
                  <Route index element={<Navigate to="/admin/users" replace />} />
                  <Route path="users" element={<AdminUsersPage />} />
                  <Route path="llm-models" element={<AdminLlmModelsPage />} />
                  <Route path="llm-usage" element={<AdminLlmUsagePage />} />
                  <Route path="llm-incidents" element={<AdminLlmIncidentsPage />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
